import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { GetSport, SportView, SportViews } from '@img-arena/consoles-api-streaming'
import { useTheme } from '@mui/material'
import { PieChart } from '@/components/charts/PieChart'
import { HorizontalTable } from '@/components/horizontalTable/HorizontalTable'
import { HorizontalTableSkeleton } from '@/components/horizontalTable/HorizontalTableSkeleton'
import { streamingRestApi } from '@/service/streamingApi'
import { bytesToHumanReadable } from '@/utils/bytesToHumanReadable'
import { getRandomColor } from '@/utils/colors'
import { formatNumberToSI } from '@/utils/formatNumberToSI'
import { secondsToHMS } from '@/utils/secondsToHMS'

import { ChartDataItem } from '../../../charts/types'
import { StatisticsCard, StatisticsCardSkeleton } from '../../StatisticsCard'
import { STATISTICS_CSV_HEADERS } from '../constants'
import { ComponentWithStatisticsQueryParams, StatisticsCardTableData } from '../types'
import { SPORT_POPULARITY_ROWS } from './sportPopularity.table'

export const SPORT_POPULARITY_CARD_TEST_ID = 'sport-popularity-card' as const
export const SPORT_POPULARITY_SKELETON_TEST_ID = 'sport-popularity-skeleton' as const

const CSV_HEADERS = [
  STATISTICS_CSV_HEADERS.SPORT_ID,
  STATISTICS_CSV_HEADERS.SPORT_NAME,
  STATISTICS_CSV_HEADERS.TOTAL_VIEWS,
  STATISTICS_CSV_HEADERS.TOTAL_BYTES,
  STATISTICS_CSV_HEADERS.TOTAL_DURATON,
  STATISTICS_CSV_HEADERS.AVERAGE_DURATION,
  STATISTICS_CSV_HEADERS.PERCENTAGE_VIEWS,
  STATISTICS_CSV_HEADERS.UNIQUE_VIEWS
]

export type SportPopularityTableData = StatisticsCardTableData<SportView>

export const SportPopularity: FC<ComponentWithStatisticsQueryParams> = ({ queryParams }) => {
  const analyticsEndpoints = streamingRestApi.endpoints.analytics
  const sportsEndpoints = streamingRestApi.endpoints.sports
  const theme = useTheme()

  const sportColorMap = {
    ...theme.palette.sportColors,
    FOOTBALL: theme.palette.sportColors.SOCCER
  }
  const [sportPopularityData, setSportPopularityData] = useState<SportViews['views'] | null>(null)
  const [sports, setSports] = useState<GetSport[]>()
  const [highlightedItem, setHighlightedItem] = useState<ChartDataItem | null>(null)

  useEffect(() => {
    const ac = new AbortController()

    sportsEndpoints
      .getSports({
        signal: ac.signal
      })
      .then((data) => {
        setSports(data)
      })

    return () => {
      ac.abort()
    }
  }, [])

  useEffect(() => {
    const ac = new AbortController()

    analyticsEndpoints.getSportViews(queryParams, ac.signal).then((data) => {
      setSportPopularityData(data.views)
    })

    return () => {
      ac.abort()
    }
  }, [...Object.values(queryParams)])

  const getSportColor = useCallback(
    (sport?: string) => {
      if (!sport) return getRandomColor()

      const sportName = sport.toUpperCase().replace(/\s+/g, '-') as keyof typeof sportColorMap

      return sportColorMap[sportName]
    },
    [sportColorMap]
  )

  const tableData: SportPopularityTableData[] = useMemo(() => {
    if (!sportPopularityData) return []

    return sportPopularityData.map((item) => {
      const sport = sports?.find((sport) => sport.id === item.sportId)
      const { background, color } = getSportColor(sport?.name)

      const {
        sportId,
        averageDuration,
        percentageViews,
        totalBytes,
        totalDuration,
        totalViews,
        uniqueViews
      } = item

      if (!sport || !sport.name) {
        return {
          sportId,
          averageDuration,
          percentageViews,
          totalBytes,
          totalViews,
          totalDuration,
          uniqueViews,
          label: 'Unknown Sport (' + item.sportId + ')',
          color: background,
          textColor: color
        }
      }

      return {
        sportId,
        averageDuration,
        percentageViews,
        totalBytes,
        totalViews,
        totalDuration,
        uniqueViews,
        label: sport.name,
        color: background,
        textColor: color
      }
    })
  }, [sportPopularityData, sports])

  const chartData = useMemo(() => {
    if (!tableData) return []

    return tableData.reduce((acc, item) => {
      const sport = sports?.find((sport) => sport.id === item.sportId)

      if (!sport || !sport.name) {
        acc.push({
          id: item.sportId,
          value: item.totalViews,
          label: 'Unknown Sport (' + item.sportId + ')',
          color: item.color,
          textColor: item.textColor,
          visible: true
        })

        return acc
      }

      acc.push({
        id: item.sportId,
        value: item.totalViews,
        label: sport.name,
        color: item.color,
        textColor: item.textColor,
        visible: true
      })

      return acc
    }, [] as ChartDataItem[])
  }, [tableData, sports])

  const csvData = useMemo(() => {
    if (!tableData.length) return

    const data = tableData.map(
      ({
        sportId,
        label,
        totalViews,
        totalBytes,
        totalDuration,
        averageDuration,
        percentageViews,
        uniqueViews
      }) => ({
        [STATISTICS_CSV_HEADERS.SPORT_ID]: sportId,
        [STATISTICS_CSV_HEADERS.SPORT_NAME]: label,
        [STATISTICS_CSV_HEADERS.TOTAL_VIEWS]: formatNumberToSI(totalViews),
        [STATISTICS_CSV_HEADERS.TOTAL_BYTES]: bytesToHumanReadable(totalBytes),
        [STATISTICS_CSV_HEADERS.TOTAL_DURATON]: formatNumberToSI(totalDuration),
        [STATISTICS_CSV_HEADERS.AVERAGE_DURATION]: secondsToHMS(averageDuration),
        [STATISTICS_CSV_HEADERS.PERCENTAGE_VIEWS]: formatNumberToSI(percentageViews),
        [STATISTICS_CSV_HEADERS.UNIQUE_VIEWS]: formatNumberToSI(uniqueViews)
      })
    )

    return {
      csvHeaders: CSV_HEADERS,
      data
    }
  }, [tableData])

  if (!sportPopularityData || !sports) {
    return (
      <StatisticsCardSkeleton testId={SPORT_POPULARITY_SKELETON_TEST_ID} height={500}>
        <HorizontalTableSkeleton columns={10} rows={4} />
      </StatisticsCardSkeleton>
    )
  }

  return (
    <StatisticsCard
      title="Sport Popularity"
      testId={SPORT_POPULARITY_CARD_TEST_ID}
      csvData={csvData}
    >
      <PieChart mb={2} onHighlightChange={setHighlightedItem} chartData={chartData} />
      <HorizontalTable
        data={tableData}
        rows={SPORT_POPULARITY_ROWS}
        shouldHighlightColumn={(column) => column.sportId === highlightedItem?.id}
      />
    </StatisticsCard>
  )
}
